import { alpha, Box, Chip, Flex, InputBase, Text } from '@shared/components';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

const DEFAULT_MIN_HEIGHT = 200;
const DEFAULT_MAX_HEIGHT = 600;
const COLLAPSE_LIMIT = 4;

function parseQIds(text: string): string[] {
  return (
    text
      // parse QID using whitespaces and new line
      .split(/[\s,]+/)
      .map((t) => t.trim())
      .filter(Boolean)
  );
}

export interface QIdChipInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  isInvalid: (qId: string) => boolean;
  max: number;
  onCapped?: (capped: boolean) => void;
  placeholder?: string;
  error?: boolean;
  minHeight?: number;
  maxHeight?: number;
}

export interface QIdChipInputHandle {
  flush: () => string[];
}

const QIdChipInput = forwardRef<QIdChipInputHandle, QIdChipInputProps>(function QIdChipInput(
  {
    value,
    onChange,
    isInvalid,
    max,
    onCapped,
    placeholder,
    error,
    minHeight = DEFAULT_MIN_HEIGHT,
    maxHeight = DEFAULT_MAX_HEIGHT,
  }: QIdChipInputProps,
  ref,
) {
  const [inputValue, setInputValue] = useState('');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const expandedHeight = useRef<number | null>(null);

  useImperativeHandle(
    ref,
    () => ({
      flush: () => {
        const pending = parseQIds(inputValue);
        if (pending.length === 0) return value;
        const combined = [...value, ...pending];
        onChange(combined);
        setInputValue('');
        return combined;
      },
    }),
    [value, inputValue, onChange],
  );

  // cap=true (paste): truncate to `max` and flag capped. cap=false (typing): append freely, parent handle error
  const commit = (tokens: string[], cap: boolean) => {
    if (tokens.length === 0) return;
    const combined = [...value, ...tokens];
    if (cap && combined.length > max) {
      onChange(combined.slice(0, max));
      onCapped?.(true);
    } else {
      onChange(combined);
      onCapped?.(false);
    }
    setInputValue('');
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',' || event.key === ' ') {
      if (inputValue.trim()) commit(parseQIds(inputValue), false);
      if (event.key !== 'Enter') event.preventDefault();
      return;
    }
    if (event.key === 'Backspace' && inputValue === '' && value.length > 0) {
      onChange(value.slice(0, -1));
      onCapped?.(false);
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const parsed = parseQIds(event.clipboardData.getData('text') || '');
    if (parsed.length === 0) return;
    event.preventDefault();
    commit(parsed, true);
  };

  const handleBlur = () => {
    setFocused(false);
    if (boxRef.current) {
      expandedHeight.current = boxRef.current.offsetHeight;
      boxRef.current.style.height = '';
    }
    if (inputValue.trim()) commit(parseQIds(inputValue), false);
  };

  useEffect(() => {
    const el = boxRef.current;
    if (focused && el && expandedHeight.current != null) {
      el.style.height = `${expandedHeight.current}px`;
    }
  }, [focused]);

  const prevLen = useRef(value.length);
  useEffect(() => {
    const el = boxRef.current;
    if (focused && el && value.length > prevLen.current) el.scrollTop = el.scrollHeight;
    prevLen.current = value.length;
  }, [value, focused]);

  const removeAt = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
    onCapped?.(false);
    if (focused) inputRef.current?.focus();
  };

  const shouldCollapse = !focused && value.length > COLLAPSE_LIMIT;
  const visibleChips = shouldCollapse ? value.slice(0, COLLAPSE_LIMIT) : value;
  const hiddenCount = value.length - visibleChips.length;

  return (
    <Box
      ref={boxRef}
      onClick={() => inputRef.current?.focus()}
      sx={(theme) => {
        const accent = error ? theme.palette.error.main : theme.palette.primary.main;
        return {
          display: 'block',
          cursor: 'text',
          p: theme.spacing(2),
          px: theme.spacing(4),
          borderRadius: theme.shape.rounded(1),
          border: `1px solid ${error ? theme.palette.error.main : theme.palette.grey[300]}`,
          '&:hover': { borderColor: accent },
          ...(focused
            ? {
                height: minHeight,
                minHeight,
                maxHeight,
                overflow: 'auto',
                resize: 'vertical',
                pb: theme.spacing(4),
                borderColor: accent,
                boxShadow: `${alpha(accent, 0.25)} 0 0 0 3px`,
              }
            : { overflow: 'hidden' }),
        };
      }}
    >
      <Flex
        sx={{
          flexWrap: focused ? 'wrap' : 'nowrap',
          gap: 1,
          alignItems: 'center',
          minWidth: 0,
        }}
      >
        {visibleChips.map((qId, index) => {
          const invalid = isInvalid(qId);
          return (
            <Chip
              key={`${qId}-${index}`}
              size='small'
              label={qId}
              color={invalid ? 'error' : 'default'}
              onMouseDown={(e) => e.preventDefault()}
              onDelete={(e) => {
                e.stopPropagation();
                removeAt(index);
              }}
              sx={(theme) => ({
                flexShrink: 0,
                maxWidth: 'none',
                height: 'auto',
                py: theme.spacing(1.5),
                pl: theme.spacing(4),
                pr: theme.spacing(2),
                '& .MuiChip-label': { overflow: 'visible' },
                ...(invalid && {
                  '&.MuiChip-colorError .MuiChip-deleteIcon': {
                    color: 'error.main',
                    '&:hover': { color: 'error.dark' },
                  },
                }),
              })}
            />
          );
        })}
        {shouldCollapse ? (
          <Text component='span' variant='body2' sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
            +{hiddenCount}
          </Text>
        ) : null}
        <InputBase
          inputRef={inputRef}
          value={inputValue}
          placeholder={value.length === 0 ? placeholder : ''}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onFocus={() => setFocused(true)}
          onBlur={handleBlur}
          sx={{ flex: '1 0 120px', minWidth: 120, '& input': { p: 0 } }}
        />
      </Flex>
    </Box>
  );
});

export default QIdChipInput;
